import type { ApprovalRule } from '../approvals.js';
import { DomainError } from '../errors.js';

/**
 * ISM formunun onay zinciri (ADR 0071; sahip kararı 2026-10-17, sorular #627). Zincir form başına
 * ofiste seçilir (`documents.approval_chain`); boşsa onay matrisinin kuralı geçerlidir (ADR 0042
 * §10, hazır ayar). Yapı sabittir:
 *
 * 1. **Hazırlayanlar** (`prepare`): 1 ya da 2 rütbe, paralel. Gönderen bunlardan birinde olmalıdır;
 *    gönderim onun hazırlayan imzasıdır. "Hepsi" seçiliyse öteki rütbe ayrıca imzalar.
 * 2. **Ön onaylar** (`review`): 0–2 adım, her biri tek rütbe, sıralı.
 * 3. **Master** (`master`): tek rütbe; ön onaylar bitmeden karar veremez.
 * 4. **Ofis** (`office`): isteğe bağlı, 1–2 rol (ör. DPA ya da Superintendent).
 *
 * Her adımda "hepsi" (`all`: her rütbe ayrı kişiyle imzalar) ya da "biri yeter" (`any`) seçilir;
 * tek rütbeli adımda ikisi aynıdır. Rütbe kişinin gemi ataması rolüdür (ADR 0071 §1). Bu modül
 * saftır.
 */

export const CHAIN_STEP_KINDS = ['prepare', 'review', 'master', 'office'] as const;
export type ChainStepKind = (typeof CHAIN_STEP_KINDS)[number];

export const CHAIN_QUORUMS = ['all', 'any'] as const;
export type ChainQuorum = (typeof CHAIN_QUORUMS)[number];

export interface ApprovalChainStep {
  kind: ChainStepKind;
  roleIds: string[];
  quorum: ChainQuorum;
}

export interface ApprovalChain {
  steps: ApprovalChainStep[];
}

/**
 * Gönderim anının zinciri (`submission_rule_inputs.chain`; ADR 0071 §3): değiştirilemez. Durum,
 * karar ve bulut denetimi kuralı bundan kurar; sonradan değişen form ayarı akışı değiştirmez.
 * `submittedAs` gönderenin hazırlayan rütbesidir.
 */
export interface ApprovalChainSnapshot extends ApprovalChain {
  submittedAs: string;
}

/** Zincirdeki adımların süresi, saat (ISM formunun varsayılanı; ADR 0042 §10) [MEPC.312 4.3.10]. */
export const CHAIN_DEADLINE_HOURS = 72;

/** Ön onay adımı en çok (sahip kararı: 1 ya da 2; 0 "hazırlayan → Master"dır). */
export const MAX_REVIEW_STEPS = 2;

const invalid = (why: string): DomainError =>
  new DomainError('INVALID_APPROVAL_CHAIN', why, 'shape');

/** Adımdaki rol sayısı sınırları. */
const ROLE_LIMITS: Readonly<Record<ChainStepKind, readonly [number, number]>> = {
  prepare: [1, 2],
  review: [1, 1],
  master: [1, 1],
  office: [1, 2],
};

function parseStep(value: unknown): ApprovalChainStep {
  if (typeof value !== 'object' || value === null) throw invalid('step');
  const step = value as Record<string, unknown>;
  const kind = step.kind;
  if (typeof kind !== 'string' || !(CHAIN_STEP_KINDS as readonly string[]).includes(kind)) {
    throw invalid('kind');
  }
  const quorum = step.quorum;
  if (typeof quorum !== 'string' || !(CHAIN_QUORUMS as readonly string[]).includes(quorum)) {
    throw invalid('quorum');
  }
  const roleIds = step.roleIds;
  const [min, max] = ROLE_LIMITS[kind as ChainStepKind];
  if (
    !Array.isArray(roleIds) ||
    roleIds.length < min ||
    roleIds.length > max ||
    roleIds.some((id) => typeof id !== 'string' || id === '') ||
    new Set(roleIds).size !== roleIds.length
  ) {
    throw invalid('roles');
  }
  return {
    kind: kind as ChainStepKind,
    roleIds: [...(roleIds as string[])],
    quorum: quorum as ChainQuorum,
  };
}

/**
 * Zincir ayarını doğrular (ofiste kayıtta ve gemide okumada): sıra `prepare` → `review`×(0–2) →
 * `master` → `office`?; rol sayıları sınırda; bir rütbe zincirin gemi adımlarında iki kez geçmez
 * (`CHAIN_ROLE_REPEATED`; dört göz, mimari belge §7.1). `roleIds` verilirse her rol şirkette
 * olmalıdır (`UNKNOWN_ROLE`).
 */
export function parseApprovalChain(value: unknown, roleIds?: readonly string[]): ApprovalChain {
  if (typeof value !== 'object' || value === null) throw invalid('chain');
  const raw = (value as { steps?: unknown }).steps;
  if (!Array.isArray(raw)) throw invalid('steps');
  const steps = raw.map(parseStep);
  const kinds = steps.map((step) => step.kind);
  const reviews = kinds.filter((kind) => kind === 'review').length;
  const office = kinds.at(-1) === 'office' ? 1 : 0;
  const expected: ChainStepKind[] = [
    'prepare',
    ...Array<ChainStepKind>(reviews).fill('review'),
    'master',
    ...(office ? (['office'] as const) : []),
  ];
  if (reviews > MAX_REVIEW_STEPS || kinds.join() !== expected.join()) throw invalid('order');
  const ship = steps.filter((step) => step.kind !== 'office').flatMap((step) => step.roleIds);
  if (new Set(ship).size !== ship.length) {
    throw new DomainError('CHAIN_ROLE_REPEATED', ship.join(','), 'roles');
  }
  if (roleIds) {
    const known = new Set(roleIds);
    for (const id of steps.flatMap((step) => step.roleIds)) {
      if (!known.has(id)) throw new DomainError('UNKNOWN_ROLE', id, 'roles');
    }
  }
  return { steps };
}

/** Anlık kopyayı doğrular: zincir geçerli ve `submittedAs` hazırlayan rütbelerinden biri. */
export function parseChainSnapshot(value: unknown): ApprovalChainSnapshot {
  const chain = parseApprovalChain(value);
  const submittedAs = (value as { submittedAs?: unknown }).submittedAs;
  if (typeof submittedAs !== 'string' || !preparers(chain).includes(submittedAs)) {
    throw invalid('submittedAs');
  }
  return { ...chain, submittedAs };
}

const preparers = (chain: ApprovalChain): string[] => chain.steps[0]?.roleIds ?? [];

/**
 * Gönderenin hazırlayan rütbesi: zincirin hazırlayanlarından gönderende olan ilki. Yoksa
 * `NOT_A_PREPARER` (ADR 0071 §3): form, zincirin hazırlayan rütbesinden biri tarafından gönderilir;
 * taslak kaybolmaz.
 */
export function preparerRoleFor(chain: ApprovalChain, submitterRoleIds: readonly string[]): string {
  const role = preparers(chain).find((id) => submitterRoleIds.includes(id));
  if (!role) throw new DomainError('NOT_A_PREPARER', preparers(chain).join(','));
  return role;
}

/**
 * Anlık kopyadan onay kuralı (ADR 0071 §3): giren roller hazırlayanlardır; gönderenin hazırlayan
 * imzası gönderimin kendisidir. "Hepsi" seçili iki hazırlayanda öteki rütbe ilk adımda imzalar
 * ("biri yeter"de bu adım yoktur). Sonra ön onaylar, Master ve varsa ofis adımı; her adım kendi
 * rolleri ve kuralıyla. Süre ISM formunun varsayılanıdır.
 */
export function chainRule(snapshot: ApprovalChainSnapshot): ApprovalRule {
  const [prepare, ...rest] = snapshot.steps;
  if (!prepare) throw invalid('steps');
  const others =
    prepare.quorum === 'all' ? prepare.roleIds.filter((id) => id !== snapshot.submittedAs) : [];
  const steps: ApprovalChainStep[] = [
    ...(others.length > 0
      ? [{ kind: 'prepare' as const, roleIds: others, quorum: 'all' as const }]
      : []),
    ...rest,
  ];
  return {
    recordType: 'ism_form',
    enteredBy: [...prepare.roleIds],
    steps: steps.map((step) => step.roleIds[0] as string),
    stepRoles: steps.map((step) => [...step.roleIds]),
    stepQuorum: steps.map((step) => step.quorum),
    stepKinds: steps.map((step) => step.kind),
    officeSteps: steps.map((step) => step.kind === 'office'),
    deadlineHours: CHAIN_DEADLINE_HOURS,
  };
}
